// The stages an operator can give a model of their own, with no imports so
// the admin UI can name them too. Each dispatch site names its stage when
// it resolves its pair (src/sandbox/platform-agent.ts).

/**
 * The stages that can carry their own model. A stage with no model of its
 * own runs on the platform model.
 */
export const AGENT_STAGES = [
  "refinement",
  "implementation",
  "review",
  "verification",
  "documentation",
  "deployment",
  "pull_request",
] as const;

/** One of the stages that can carry its own model. */
export type AgentStage = (typeof AGENT_STAGES)[number];

/**
 * The runtime a stage's agent runs on. Refinement and the pull request
 * description are pi sessions in the control plane whatever the platform
 * runtime (ADR-0034 §1); every other stage runs in a sandbox on the
 * platform runtime.
 */
export function runtimeOfStage(stage: AgentStage, platformRuntime: string): string {
  return stage === "refinement" || stage === "pull_request" ? "pi" : platformRuntime;
}

// The stage each Run kind dispatches at. The three script kinds run no
// agent and have no stage.
const STAGE_OF_RUN_KIND: Readonly<Record<string, AgentStage>> = {
  refinement: "refinement",
  implementation: "implementation",
  review: "review",
  verify: "verification",
  integrate: "verification",
  try: "verification",
  documentation: "documentation",
  services: "deployment",
  automation: "deployment",
};

/**
 * The stage a Run of this kind dispatches its agent at, or null for a kind
 * that runs a script and no agent (deploy, teardown, audit).
 */
export function stageOfRunKind(kind: string): AgentStage | null {
  return STAGE_OF_RUN_KIND[kind] ?? null;
}

/** The `platform_setting` key of a stage's own model; an empty value means the platform model. */
export function stageModelKeyOf(stage: AgentStage): string {
  return `model_${stage}`;
}
